import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { HTTPError } from 'ky';
import isEqual from 'lodash/isEqual';
import Card from '@components-clean/core/card';
import CoreCheckbox from '@components-clean/core/core-checkbox';
import EditCard from '@components-clean/core/edit-card';
import { InfoField } from '@components-clean/core/info-field';
import Loading from '@components-clean/core/loading/loading';
import { IsPopOut } from '@components-clean/core/page-toolbar/page-toolbar';
import { TextArea } from '@components-clean/core/text-area';
import { useUnsavedChangesContext } from '@contexts-clean/unsaved-changes-context';
import IHubDataset from '@pages-clean/central-monitoring/dataset-hub/interfaces/hub-dataset.interface';
import { useAuthorization } from '@hooks/index';
import Constants from '@utils/constants';
import { ShowError, ShowSuccess } from '@utils/notification';
import DatasetApi from '@apis/bff-api/central-monitoring/dataset';
import { UnprocessableEntityResponse } from '@apis/rest-client';

export const COMPONENT_NAME = 'dataset-hub-notes';

interface IProps {
    studyRef: string;
    dataset?: IHubDataset;
    cardTitle: React.ReactNode;
}

const DatasetNotes = ({ studyRef, dataset, cardTitle }: IProps) => {
    const user = useAuthorization();
    if (!user) throw new Error('User not found');

    const { setComponentHasChanges, componentHasChanges, confirmDiscardChanges } = useUnsavedChangesContext();

    const {
        t,
        i18n: { t: tRoot },
    } = useTranslation('cm', {
        keyPrefix: 'datasetHub.tabs.datasetInfo.chips.datasetNotes',
    });

    interface IFormData {
        isActive: boolean;
        notes: string;
    }

    interface IFormValidationConfig {
        notes: {
            maxLength: number;
        };
    }

    const formValidationConfig: IFormValidationConfig = {
        notes: {
            maxLength: 4000,
        },
    };

    const EMPTY_FORM: IFormData = {
        isActive: false,
        notes: '',
    };

    const { data, isLoading: isFetching } = DatasetApi.useGetNotes(studyRef, dataset?.id);
    const { trigger: save, isMutating: isSaving } = DatasetApi.useSaveNotes(studyRef, dataset?.id);

    const isLoading = isFetching || isSaving;

    const [originalFormData, setOriginalFormData] = useState<IFormData>(EMPTY_FORM);
    const [formData, setFormData] = useState<IFormData>(EMPTY_FORM);
    const [isEditing, setIsEditing] = useState(false);

    const resetForm = () => {
        if (!data) {
            setFormData(EMPTY_FORM);
            return;
        }

        setFormData({
            isActive: data.isActive,
            notes: data.notes ?? '',
        });
    };

    // Reset the form when the current dataset changes.
    useEffect(() => {
        resetForm();
        setIsEditing(false);

        // set the original form data to detect changes,
        // which will allow the unsaved changes dialog to display
        setOriginalFormData({
            isActive: data?.isActive ?? false,
            notes: data?.notes ?? '',
        });
    }, [data]);

    // Reset the form when the form is no longer being edited
    useEffect(() => {
        if (isEditing) return;
        resetForm();
    }, [isEditing]);

    useEffect(() => {
        if (!data) return;

        const isDirty = !isEqual(originalFormData, formData);
        if (isDirty !== componentHasChanges(COMPONENT_NAME)) setComponentHasChanges(COMPONENT_NAME, isDirty);
    }, [formData]);

    const isValid = (form: IFormData): boolean => {
        const formValidationErrors: string[] = [];

        if ((form.notes?.length ?? 0) > formValidationConfig.notes.maxLength) {
            formValidationErrors.push(
                `"${t('labels.datasetNotes')}" cannot exceed ${formValidationConfig.notes.maxLength} characters`,
            );
        }

        if (formValidationErrors.length === 0) {
            return true;
        }

        ShowError(`There are form validation errors:${formValidationErrors.map((error) => ` ${error}`).join(', ')}`);
        return false;
    };

    const onSaveHandler = async () => {
        try {
            if (!isValid(formData)) {
                return;
            }

            const body = {
                id: dataset!.id,
                isActive: formData.isActive,
                notes: formData.notes.replace(/\s/g, '') === '' ? null : formData.notes,
            };
            await save(body);
            ShowSuccess(tRoot('actionsSuccessfulToast'));
            await DatasetApi.invalidateGetAll(studyRef);
            setIsEditing(false);
            setComponentHasChanges(COMPONENT_NAME, false);
        } catch (error) {
            let errorMessage = t('saveError');

            if (error instanceof HTTPError) {
                if (error.response.status === 422) {
                    // HTTP 422 - Unprocessable Entity
                    const response: UnprocessableEntityResponse = await error.response?.json();
                    errorMessage = response.message;
                }
            }

            ShowError(errorMessage);
        }
    };

    const content = (
        <>
            {!dataset ? (
                <div className="no-data-content">
                    <InfoField type="Info" message={tRoot('cm:datasetHub:selectRecord')} layout="center" />
                </div>
            ) : (
                <div className="form">
                    <CoreCheckbox
                        data-testid="is-active"
                        label={t('labels.isActive')}
                        checked={formData.isActive}
                        disabled={!isEditing}
                        onChange={(e) => {
                            setFormData({
                                ...formData,
                                isActive: e.value,
                            });
                        }}
                    />
                    <div className="notes-input">
                        <TextArea
                            label={t('labels.datasetNotes')}
                            data-testid="notes"
                            readOnly={!isEditing}
                            value={formData.notes}
                            maxLength={formValidationConfig.notes.maxLength}
                            onChange={(e) => {
                                setFormData({
                                    ...formData,
                                    notes: e.value,
                                });
                            }}
                        />
                    </div>
                </div>
            )}
            <div className={`loading-overlay ${!isLoading ? 'fade-out' : ''}`}>
                <Loading />
            </div>
        </>
    );

    const props = {
        title: cardTitle,
        className: 'dataset-notes-card',
    };

    if (dataset && user.systemAdminLevel > Constants.SystemAdminLevel.Reviewer && !IsPopOut()) {
        return (
            <EditCard
                {...props}
                bodyClassName="k-pos-relative card-body"
                data-testid="dataset-notes-edit-card"
                isEditMode={isEditing}
                onEdit={() => setIsEditing(true)}
                onSave={onSaveHandler}
                onCancel={async () => {
                    if (componentHasChanges(COMPONENT_NAME)) {
                        const confirmed = await confirmDiscardChanges();
                        if (!confirmed) return;
                    }
                    setIsEditing(false);
                }}
                disabled={
                    !data ||
                    (!data.isEditable && {
                        tooltip: {
                            text: t('processingEditButton.tooltip'),
                            position: 'left',
                        },
                        button: { text: t('processingEditButton.text') },
                    })
                }
            >
                {content}
            </EditCard>
        );
    }

    return (
        <Card {...props} data-testid="dataset-notes-card">
            {content}
        </Card>
    );
};

export default DatasetNotes;
